import { useState } from 'react';
import { RefreshCw, Zap } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthor } from '@/hooks/useAuthor';
import { useGround } from '@/hooks/useGround';
import { useGroundFeed } from '@/hooks/useGroundFeed';
import { categoryMeta, groundCategory, safeImageUrl, shortId, timeAgo } from '@/lib/ground/utils';
import { cn } from '@/lib/utils';
import { ZapDialog } from './ZapDialog';

import type { NostrEvent } from '@nostrify/nostrify';

function FeedItem({ event }: { event: NostrEvent }) {
  const author = useAuthor(event.pubkey);
  const { identity, zapsByEvent } = useGround();
  const [zapOpen, setZapOpen] = useState(false);

  const metadata = author.data?.metadata;
  const isSelf = event.pubkey === identity.pubkey;
  const name = isSelf
    ? 'You'
    : metadata?.display_name || metadata?.name || shortId(event.pubkey);
  const picture = safeImageUrl(metadata?.picture);
  const category = groundCategory(event);
  const meta = categoryMeta(category);
  const zapTotal = zapsByEvent[event.id] ?? 0;

  return (
    <Card className={cn('transition-colors', isSelf && 'border-primary/30')}>
      <CardContent className="py-5">
        <div className="flex items-start gap-3">
          <Avatar size="lg">
            {picture && <AvatarImage src={picture} alt="" />}
            <AvatarFallback className="bg-secondary font-display text-sm">
              {name.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="truncate font-medium">{name}</span>
              {isSelf && (
                <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-normal">
                  local key
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground">{timeAgo(event.created_at)}</span>
              <Badge variant="outline" className="ml-auto gap-1 font-normal">
                <span aria-hidden>{meta.emoji}</span>
                {meta.label}
              </Badge>
            </div>

            <p className="mt-2 whitespace-pre-wrap break-words text-[15px] leading-relaxed text-foreground/90">
              {event.content}
            </p>

            <div className="mt-3 flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-muted-foreground hover:text-primary"
                onClick={() => setZapOpen(true)}
              >
                <Zap className="size-4" />
                {zapTotal > 0 ? `${zapTotal.toLocaleString()} sats` : 'Zap'}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>

      <ZapDialog eventId={event.id} open={zapOpen} onOpenChange={setZapOpen} />
    </Card>
  );
}

function FeedSkeleton() {
  return (
    <Card>
      <CardContent className="py-5">
        <div className="flex items-start gap-3">
          <Skeleton className="size-10 rounded-full" />
          <div className="flex-1 space-y-3">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-7 w-24" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function GroundFeed() {
  const { data, isLoading, isError, isFetching, refetch } = useGroundFeed();
  const { identity } = useGround();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
            The Ground Feed
          </h2>
          <p className="mt-3 text-muted-foreground">
            Every practice completed on Ground is shared to Nostr. Here is what the community is
            doing right now.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2">
          <RefreshCw className={cn('size-4', isFetching && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      <div className="flex items-center gap-2 rounded-lg border border-dashed px-4 py-3 text-xs text-muted-foreground">
        <span className="size-2 shrink-0 rounded-full bg-primary" />
        Posting as {identity.isGuest ? 'a local demo identity' : 'your Nostr account'}{' '}
        <span className="truncate font-mono text-[11px] text-foreground/70">{shortId(identity.npub, 10)}</span>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <FeedSkeleton />
          <FeedSkeleton />
          <FeedSkeleton />
        </div>
      ) : isError ? (
        <Card className="border-dashed">
          <CardContent className="px-8 py-12 text-center">
            <p className="mx-auto max-w-sm text-muted-foreground">
              Couldn&rsquo;t reach the relays. Check your connection and try refreshing.
            </p>
          </CardContent>
        </Card>
      ) : data && data.length > 0 ? (
        <div className="space-y-4">
          <Separator />
          {data.map((event) => (
            <FeedItem key={event.id} event={event} />
          ))}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="px-8 py-12 text-center">
            <p className="mx-auto max-w-sm text-muted-foreground">
              No practices shared yet. Be the first — complete a session, a roadmap step, or a
              nature reconnection and it will appear here.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
